/**
 * Utility functions for custom request date validation and formatting.
 * Ported from executive app's custom-request-utils.ts.
 * @module Widget/CustomRequest/utils
 */

import dayjs from 'dayjs';
import type { AvailDate, RestaurantResponseData } from '@/lib/types.ts';
import type { CustomRequestPriceOption } from '../lib/types.ts';

type DayOfWeek = 'sun' | 'mon' | 'tue' | 'wed' | 'thu' | 'fri' | 'sat';

const DAY_MAP: DayOfWeek[] = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];

/**
 * Checks if a restaurant accepts custom requests for a specific date.
 * Validates both day-of-week rules and date-specific blocks.
 *
 * @param restaurant - Restaurant data with special_request_days
 * @param date - The date to check (YYYY-MM-DD string or Date object)
 * @param availDates - Optional per-date availability from the API
 * @returns true if custom requests are allowed for this date
 *
 * @example
 * isCustomRequestAllowedForDate(restaurant, '2026-03-15', availDates)
 */
export function isCustomRequestAllowedForDate(
  restaurant: RestaurantResponseData | null | undefined,
  date: string | Date,
  availDates?: AvailDate[]
): boolean {
  if (!restaurant) return false;

  const d = typeof date === 'string' ? dayjs(date) : dayjs(date);
  if (!d.isValid()) return false;

  const specialRequestDays = restaurant.special_request_days;
  if (!specialRequestDays) return false;

  const dayOfWeek = DAY_MAP[d.day()];
  if (!specialRequestDays[dayOfWeek]) return false;

  if (availDates && availDates.length > 0) {
    const dateStr = d.format('YYYY-MM-DD');
    const dateEntry = availDates.find((entry) => entry.date === dateStr);
    if (dateEntry && !dateEntry.allow_special_request) return false;
  }

  return true;
}

/**
 * Checks if a restaurant has custom requests enabled on any day.
 *
 * @param restaurant - Restaurant data
 * @returns true if at least one day allows custom requests
 */
export function hasCustomRequestsEnabled(
  restaurant: RestaurantResponseData | null | undefined
): boolean {
  if (!restaurant?.special_request_days) return false;
  const days = restaurant.special_request_days;
  return !!(days.sun || days.mon || days.tue || days.wed || days.thu || days.fri || days.sat);
}

/**
 * Converts a display time (e.g., "7:00 PM") to HH:mm format (e.g., "19:00").
 *
 * @param displayTime - Time in display format
 * @returns Time in HH:mm format
 */
export function displayTimeToHHmm(displayTime: string): string {
  const parsed = dayjs(`2000-01-01 ${displayTime}`, 'YYYY-MM-DD h:mm A');
  if (!parsed.isValid()) return displayTime;
  return parsed.format('HH:mm');
}

/**
 * Converts a 24h time string (e.g., "17:00") to 12h display (e.g., "5:00pm").
 *
 * @param time24 - Time in HH:mm format
 * @returns Time in 12h format like "5:00pm"
 * @example formatTime24to12("17:30") // "5:30pm"
 */
export function formatTime24to12(time24: string): string {
  const [hoursStr, minutesStr] = time24.split(':');
  let hours = parseInt(hoursStr, 10);
  const minutes = minutesStr ?? '00';
  if (isNaN(hours)) return time24;

  const period = hours >= 12 ? 'pm' : 'am';
  if (hours === 0) hours = 12;
  else if (hours > 12) hours -= 12;

  return `${hours}:${minutes}${period}`;
}

/**
 * Formats an array of 24h time strings to a readable 12h display string.
 *
 * @param times - Array of HH:mm strings
 * @returns Comma-separated 12h display string
 * @example formatTimeList(["17:00", "17:30", "18:15"]) // "5:00pm, 5:30pm, 6:15pm"
 */
export function formatTimeList(times: string[]): string {
  return [...times].sort().map(formatTime24to12).join(', ');
}

/**
 * Formats an array of 24h time strings as a range showing earliest to latest.
 *
 * @param times - Array of HH:mm strings
 * @returns Range string like "5:00pm - 8:00pm", or single time if only one
 * @example formatTimeRange(["17:00", "17:30", "20:00"]) // "5:00pm - 8:00pm"
 */
export function formatTimeRange(times: string[]): string {
  if (!times.length) return '';
  const sorted = [...times].sort();
  const first = formatTime24to12(sorted[0]);
  const last = formatTime24to12(sorted[sorted.length - 1]);
  return first === last ? first : `${first} – ${last}`;
}

/**
 * Gets acceptance probability label and color based on spend and price options.
 *
 * @param minimumSpend - Minimum spend per person
 * @param priceOptions - Available price options from API
 * @returns Object with label and Tailwind color class
 */
/** Snapshot of custom request data saved per-UUID so it's independent of shared widget state. */
export interface CustomRequestSnapshot {
  date: string;
  party_size: number;
  selected_times: string[];
  selected_table_types: number[];
  selected_table_type_names: string[];
  per_person_spend: number;
  minimum_spend: number;
  expiration_hours?: number;
  expiration_description?: string;
  comments?: string;
  payment_brand?: string;
  payment_last4?: string;
  user_first_name?: string;
  user_last_name?: string;
  user_email?: string;
  user_phone?: string;
}

const SNAPSHOT_PREFIX = 'cr_snapshot_';

/**
 * Saves a custom request snapshot to localStorage keyed by UUID.
 * @param uuid - Reservation UUID
 * @param data - Snapshot of the request data at submission time
 */
export function saveRequestSnapshot(uuid: string, data: CustomRequestSnapshot): void {
  try {
    localStorage.setItem(`${SNAPSHOT_PREFIX}${uuid}`, JSON.stringify(data));
  } catch {
    // localStorage full or unavailable — non-critical
  }
}

/**
 * Loads a custom request snapshot from localStorage by UUID.
 * @param uuid - Reservation UUID
 * @returns Snapshot data or null if not found
 */
export function loadRequestSnapshot(uuid: string): CustomRequestSnapshot | null {
  try {
    const raw = localStorage.getItem(`${SNAPSHOT_PREFIX}${uuid}`);
    return raw ? (JSON.parse(raw) as CustomRequestSnapshot) : null;
  } catch {
    return null;
  }
}

export function getAcceptanceProbability(
  minimumSpend: number,
  priceOptions?: CustomRequestPriceOption[]
): { label: string; color: string } {
  if (!priceOptions || priceOptions.length === 0) {
    return { label: 'Acceptance chance varies', color: 'text-gray-500' };
  }

  const sorted = [...priceOptions].sort((a, b) => a.price - b.price);
  const lowerOptions = sorted.filter((opt) => opt.price <= minimumSpend);
  const closestOption = lowerOptions.length > 0
    ? lowerOptions[lowerOptions.length - 1]
    : sorted[0];

  if (!closestOption) {
    return { label: 'Acceptance chance varies', color: 'text-gray-500' };
  }

  switch (closestOption.acceptance_chance) {
    case 'GOOD':
      return { label: closestOption.acceptance_label || 'Good chance', color: 'text-green-600' };
    case 'LOW':
      return { label: closestOption.acceptance_label || 'Lower chance', color: 'text-red-600' };
    default:
      return { label: closestOption.acceptance_label || 'Acceptance chance varies', color: 'text-gray-500' };
  }
}
